import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Input,
  message,
  PageHeader,
  Select,
  Form,
  Row,
  Col,
  DatePicker,
  Table,
  InputNumber,
  Space,
  Popconfirm,
  Tag,
} from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import Page from "_components/Page";
import { ReferenceTypeCodes } from "_helpers";
import {
  stockTypesService,
  purchaseOrderService,
  companyService,
  measurementTypeService,
  referenceService,
} from "_services";
import PropTypes from "prop-types";
import TabMenu from "./TabMenu";

let purchaseOrderLineIndex = 0;

function Summary({ history, match }) {
  const { id } = match.params;
  const { path } = match;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(false);
  const [isLoadingStoreAddresses, setIsLoadingStoreAddresses] = useState(false);
  const [isLoadingSupplierData, setIsLoadingSupplierData] = useState(false);
  const [stores, setStores] = useState([]);
  const [storeAddresses, setStoreAddresses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierAddresses, setSupplierAddresses] = useState([]);
  const [supplierStockTypes, setSupplierStockTypes] = useState([]);
  const [purchaseOrderLines, setPurchaseOrderLines] = useState([]);
  const [measurementTypes, setMeasurementTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [purchaseOrderTypes, setPurchaseOrderTypes] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [purchaseOrder, setPurchaseOrder] = useState();
  const [selectedStoreId, setSelectedStoreId] = useState();
  const [selectedSupplierId, setSelectedSupplierId] = useState();

  const [consignees, setConsignees] = useState([]);
  const [isLoadingConsignees, setIsLoadingConsignees] = useState(false);

  const [buyers, setBuyers] = useState([]);
  const [isLoadingBuyer, setIsLoadingBuyer] = useState(false);

  const [error, setError] = useState(null);
  const [form] = Form.useForm();
  const { Option } = Select;
  const { TextArea } = Input;

  useEffect(() => {
    getStoreCompanies();
    getSupplierCompanies();
    measurementTypeService.getSuggestion().then((data) => {
      setMeasurementTypes(data.content);
    });
    getCurrencies();
    getPurchaseOrderTypes();
    getPurchaseOrderById();
    getConsignees();
    getBuyers();
  }, []);

  const getConsignees = () => {
    setIsLoadingConsignees(true);
    companyService
      .getSuggestion("?companyTypeCodes=CON")
      .then((x) => {
        setIsLoadingConsignees(false);
        setConsignees(x.content);
      })
      .catch((errorInfo) => {
        setIsLoadingConsignees(false);
        message.error(`${errorInfo}`);
      });
  };

  const getBuyers = () => {
    setIsLoadingBuyer(true);
    companyService
      .getSuggestion("?companyTypeCodes=BUY")
      .then((x) => {
        setIsLoadingBuyer(false);
        setBuyers(x.content);
      })
      .catch((errorInfo) => {
        setIsLoadingBuyer(false);
        message.error(`${errorInfo}`);
      });
  };

  useEffect(() => {
    getStoreAddress(selectedStoreId);
  }, [selectedStoreId]);

  useEffect(() => {
    getSupplierData(selectedSupplierId);
  }, [selectedSupplierId]);

  const getPurchaseOrderById = () => {
    setIsLoading(true);
    purchaseOrderService
      .getById(id)
      .then((data) => {
        const purchaseOrderData = data.content;

        purchaseOrderData.orderDate = moment(purchaseOrderData.orderDate);
        purchaseOrderData.expectedDate = moment(purchaseOrderData.expectedDate);

        const modifiedPurchaseOrderLineData = Array.isArray(
          purchaseOrderData.purchaseOrderLines
        )
          ? purchaseOrderData.purchaseOrderLines.map((singleLine, index) => {
              const orderQuantityText = `orderQuantity[${index}]`;
              const remainingQuantity = `remainingQuantity[${index}]`;
              const unitPriceText = `unitPrice[${index}]`;
              const deliveryDateText = `deliveryDate[${index}]`;
              const expiryDateText = `expiryDate[${index}]`;
              const expectedDateText = `expectedDate[${index}]`;

              purchaseOrderData[orderQuantityText] = singleLine.orderQuantity;
              purchaseOrderData[remainingQuantity] =
                singleLine.remainingQuantity;
              purchaseOrderData[unitPriceText] = singleLine.unitPrice;
              purchaseOrderData[deliveryDateText] = moment(
                singleLine.deliveryDate
              );
              purchaseOrderData[expiryDateText] = moment(singleLine.expiryDate);
              purchaseOrderData[expectedDateText] = moment(
                singleLine.expectedDate
              );

              return {
                ...singleLine,
                key: index,
                stockType: singleLine.stockType.id,
                deliveryDate: moment(singleLine.deliveryDate),
                expiryDate: moment(singleLine.expiryDate),
                expectedDate: moment(singleLine.expectedDate),
                uom: singleLine.uom.id,
              };
            })
          : [];

        // Calculate total price for each line item n set it as default
        const totalPriceSum = modifiedPurchaseOrderLineData.reduce(
          (sum, line) => {
            return sum + (line.totalPrice ?? 0);
          },
          0
        );

        form.setFieldsValue({
          totalPrice: parseFloat(totalPriceSum),
        });

        setPurchaseOrderLines(modifiedPurchaseOrderLineData);
        setSelectedStoreId(purchaseOrderData.store.id);
        setSelectedSupplierId(purchaseOrderData.supplier.id);
        setPurchaseOrder(purchaseOrderData);
        onFill(purchaseOrderData);
      })
      .catch((error) => {
        setError(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onFill = (data) => {
    
    form.setFieldsValue(data);
  };

  const getSupplierCompanies = () => {
    setIsLoadingSuppliers(true);
    companyService
      .getSuggestion("?companyTypeCodes=SUP,MSUP")
      .then((x) => {
        setIsLoadingSuppliers(false);
        setSuppliers(x.content);
      })
      .catch((errorInfo) => {
        setIsLoadingSuppliers(false);
        message.error(`${errorInfo}`);
      });
  };

  const getStoreCompanies = () => {
    setIsLoadingStores(true);
    companyService
      .getSuggestion("?companyTypeCodes=STORE")
      .then((x) => {
        setIsLoadingStores(false);
        setStores(x.content);
      })
      .catch((errorInfo) => {
        setIsLoadingStores(false);
        message.error(`${errorInfo}`);
      });
  };

  const getStoreAddress = (companyId) => {
    if (companyId) {
      setStoreAddresses([]);
      form.resetFields(["deliveryAddress"]);
      setIsLoadingStoreAddresses(true);
      companyService
        .getById(companyId)
        .then((x) => {
          setIsLoadingStoreAddresses(false);
          setStoreAddresses(x.content.addresses);
        })
        .catch((errorInfo) => {
          message.error(`${errorInfo}`);
          setIsLoadingStoreAddresses(false);
        });
    }
  };

  const getSupplierData = (companyId) => {
    if (companyId) {
      setSupplierAddresses([]);
      setSupplierStockTypes([]);
      form.resetFields(["supplierAddress"]);
      setIsLoadingSupplierData(true);
      companyService
        .getById(companyId)
        .then((x) => {
          setIsLoadingSupplierData(false);
          setSupplierAddresses(x.content.addresses);
        })
        .catch((errorInfo) => {
          setIsLoadingSupplierData(false);
        });
      stockTypesService.getSupplierStockTypes(companyId).then((x) => {
        setSupplierStockTypes(x.content);

        if (x.content && x.content[0]?.supplierCompanies) {
          form.setFieldsValue({
            currency: x.content[0].supplierCompanies[0].currency,
          });
        }
      });
    }
  };

  const getCurrencies = () => {
    setIsLoading(true);
    referenceService
      .getAllReferenceDataByTypeCode(ReferenceTypeCodes.CURRENCY)
      .then((x) => {
        setIsLoading(false);
        setCurrencies(x.content);
      })
      .catch((errorInfo) => {
        setIsLoading(false);
        message.error(`${errorInfo}`);
      });
  };

  const getPurchaseOrderTypes = () => {
    setIsLoading(true);
    referenceService
      .getAllReferenceDataByTypeCode(ReferenceTypeCodes.PO_TYPE)
      .then((x) => {
        setIsLoading(false);
        setPurchaseOrderTypes(x.content);
      })
      .catch((errorInfo) => {
        setIsLoading(false);
        message.error(`${errorInfo}`);
      });
  };

  const cleanSubmitObject = (payload) => {
    const acceptFields = [
      "orderDate",
      "supplier",
      "supplierAddress",
      "store",
      "deliveryAddress",
      "note",
      "purchaseOrderType",
      "expectedDate",
      "purchaseOrderLines",
      "orderNumber",
      "currency",
      "consignee",
      "buyer",
    ];

    return Object.keys(payload).reduce((acc, key) => {
      if (
        acceptFields.includes(key) &&
        payload[key] !== undefined &&
        payload[key] !== null
      ) {
        acc[key] = payload[key];
      }
      return acc;
    }, {});
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        values.orderDate = moment(values.orderDate).format("YYYY-MM-DD");
        values.expectedDate = moment(values.expectedDate).format("YYYY-MM-DD");

        if (purchaseOrderLines.length <= 0) {
          throw new Error("Add at least one purchase order line.");
        } else {
          setIsLoading(true);
          values.purchaseOrderLines = purchaseOrderLines.map((item, index) => {
            const newItem = { ...item };
            newItem.stockType = { id: item.stockType };
            newItem.deliveryDate = moment(item.deliveryDate).format(
              "YYYY-MM-DD"
            );
            newItem.expectedDate = moment(item.expectedDate).format(
              "YYYY-MM-DD"
            );
            newItem.uom = { id: item.uom };

            delete newItem.key;

            return newItem;
          });
          const cleanPayload = cleanSubmitObject(values);
          
          purchaseOrderService
            .updatePurchaseOrder(cleanPayload, purchaseOrder.id)
            .then(() => {
              setIsLoading(false);
              message.success("Successfully created!");
              history.push("/purchase-orders");
            })
            .catch((error) => {
              setIsLoading(false);
              setError(error);
            });
        }
      })
      .catch((error) => {
        // Handle form validation errors properly
        if (error && typeof error === 'object' && error.errorFields) {
          // This is a form validation error, don't set it as a general error
          console.log("Form validation errors:", error.errorFields);
        } else {
          setError(error);
        }
      });
  };

  const handleAddPurchaseOrderLine = () => {
    const newPurchaseOrderLine = {
      key: purchaseOrderLineIndex,
      expectedDate: moment(),
      deliveryDate: moment(),
    };
    newPurchaseOrderLine.key = purchaseOrderLineIndex;
    setPurchaseOrderLines((preState) => [...preState, newPurchaseOrderLine]);
    purchaseOrderLineIndex += 1;
  };

  const handleFieldChange = (value, fieldName, key) => {
    const lines = [...purchaseOrderLines];
    const newOrderLines = lines.map((line) => {
      const newLine = { ...line };
      if (newLine.key === key) {
        newLine[fieldName] = value;
        if (newLine.orderQuantity && newLine.unitPrice) {
          newLine.totalPrice = newLine.orderQuantity * newLine.unitPrice;
        } else {
          delete newLine.totalPrice;
        }
        return newLine;
      }
      return newLine;
    });
    const totalAmount = newOrderLines?.reduce((sum, line) => {
      const price = parseFloat(line.totalPrice) || 0;
      return sum + price;
    }, 0);
    form.setFieldsValue({ totalPrice: totalAmount });
    setTotalPrice(totalAmount);
    setPurchaseOrderLines(newOrderLines);
  };

  const getRowByKey = (key, newData) => {
    return (purchaseOrderLines || newData).filter(
      (item) => item.key === key
    )[0];
  };

  const handleSavePurchaseOrderLine = (e, key) => {
    e.persist();
    const target = getRowByKey(key) || {};
    if (
      !target.stockType ||
      !target.orderQuantity ||
      !target.unitPrice ||
      !target.deliveryDate ||
      !target.expectedDate ||
      !target.uom
    ) {
      message.error("Please complete the purchase list item information.");
      e.target.focus();
      return;
    }
    purchaseOrderLineIndex = key + 1;
    handleAddPurchaseOrderLine();
  };

  const handleRemovePurchaseOrderLine = (key) => {
    const newPurchaseOrderLine = purchaseOrderLines.filter(
      (singleOrderLine) => {
        return singleOrderLine.key !== key;
      }
    );
    setPurchaseOrderLines(newPurchaseOrderLine);
  };

  const onCancel = () => {
    history.push("/purchase-orders/");
  };

  const purchaseOrderLineColumns = [
    {
      title: "Stock Type",
      dataIndex: "stockType",
      key: "stockType",
      width: "30%",
      render: (text, record) => {
        return (
          <Form.Item
            name={`stockType[${record.key}]`}
            key={record.key}
            style={{ marginBottom: "0" }}
            rules={[{ required: true, message: "Required." }]}
            initialValue={text}
            hasFeedback
          >
            <Select
              placeholder="Please select"
              allowClear
              showSearch
              optionFilterProp="children"
              onChange={(e) => handleFieldChange(e, "stockType", record.key)}
            >
              {supplierStockTypes.length > 0 &&
                supplierStockTypes.map((t) => (
                  <Option
                    value={t.id}
                    key={t.id}
                    disabled={purchaseOrderLines.some(
                      (singleOrderLine) => singleOrderLine.stockType === t.id
                    )}
                  >
                    {t.code}({t.secondaryCode}) - {t.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        );
      },
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: "10%",
      render: (text, record) => {
        return (
          <Form.Item
            name={`uom[${record.key}]`}
            key={record.key}
            style={{ marginBottom: "0" }}
            rules={[{ required: true, message: "Required." }]}
            initialValue={text}
            hasFeedback
          >
            <Select
              placeholder="Please select"
              allowClear
              showSearch
              optionFilterProp="children"
              onChange={(e) => handleFieldChange(e, "uom", record.key)}
            >
              {measurementTypes.length > 0 &&
                measurementTypes.map((t) => (
                  <Option value={t.id} key={t.id}>
                    {t.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        );
      },
    },
    {
      title: "Quantity",
      dataIndex: "orderQuantity",
      key: "orderQuantity",
      align: "right",
      width: "10%",
      render: (text, record) => {
        return (
          <Form.Item
            name={`orderQuantity[${record.key}]`}
            key={record.key}
            style={{ marginBottom: "0" }}
            rules={[{ required: true, message: "Required." }]}
            hasFeedback
          >
            <InputNumber
              placeholder="Units"
              style={{ width: "100%" }}
              min={record._id ? record.receivedUnits : 0}
              onChange={(e) =>
                handleFieldChange(e, "orderQuantity", record.key)
              }
            />
          </Form.Item>
        );
      },
    },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      align: "right",
      width: "10%",
      render: (text, record) => {
        return (
          <Form.Item
            name={`unitPrice[${record.key}]`}
            key={record.key}
            style={{ marginBottom: "0" }}
            rules={[{ required: true, message: "Required." }]}
            hasFeedback
          >
            <InputNumber
              placeholder="Units"
              min={record._id ? record.receivedUnits : 0}
              style={{ width: "100%" }}
              onChange={(e) => handleFieldChange(e, "unitPrice", record.key)}
            />
          </Form.Item>
        );
      },
    },
    {
      title: "Total Price",
      key: "totalPrice",
      align: "right",
      width: "10%",
      render: (text, record) => {
        return (
          <InputNumber
            placeholder="Total Price"
            value={purchaseOrderLines[record.key]?.totalPrice ?? ""}
            style={{ width: "100%" }}
            disabled
            precision={2}
            formatter={(val) =>
              val !== null && val !== undefined
                ? parseFloat(val).toFixed(2)
                : ""
            }
          />
        );
      },
    },
    {
      title: "Re.Quantity",
      dataIndex: "remainingQuantity",
      key: "remainingQuantity",
      align: "right",
      width: "10%",
      render: (text, record) => {
        return (
          <InputNumber
            placeholder="Re.Quantity"
            value={purchaseOrderLines[record.key]?.remainingQuantity ?? ""}
            style={{ width: "100%" }}
            disabled
            precision={2}
          />
        );
      },
    },
    {
      title: "Delivery Date",
      dataIndex: "deliveryDate",
      key: "deliveryDate",
      align: "right",
      width: "15%",
      render: (text, record) => {
        return (
          <Form.Item
            name={`deliveryDate[${record.key}]`}
            key={record.key}
            style={{ marginBottom: "0" }}
            rules={[{ required: true, message: "Required." }]}
            initialValue={moment()}
          >
            <DatePicker
              placeholder="Delivery date"
              format="YYYY-MM-DD"
              style={{ width: "100%" }}
              onChange={(e) => handleFieldChange(e, "deliveryDate", record.key)}
            />
          </Form.Item>
        );
      },
    },
    {
      title: "Expected Date",
      dataIndex: "expectedDate",
      key: "expectedDate",
      align: "right",
      width: "15%",
      render: (text, record) => {
        return (
          <Form.Item
            name={`expectedDate[${record.key}]`}
            key={record.key}
            style={{ marginBottom: "0" }}
            rules={[{ required: true, message: "Required." }]}
            initialValue={moment()}
          >
            <DatePicker
              placeholder="Expected date"
              format="YYYY-MM-DD"
              style={{ width: "100%" }}
              onChange={(e) => handleFieldChange(e, "expectedDate", record.key)}
            />
          </Form.Item>
        );
      },
    },
    {
      title: "",
      key: "operation",
      render: (text, record) => {
        return (
          <Space size="small">
            <Button
              size="small"
              icon={<CloseOutlined />}
              onClick={() => handleRemovePurchaseOrderLine(record.key)}
              style={{ padding: 5, fontSize: 9 }}
            ></Button>
            <Button
              type="primary"
              onClick={(e) => {
                e.preventDefault();
                handleSavePurchaseOrderLine(e, record.key);
              }}
              style={{ padding: 5, fontSize: 12 }}
            >
              +More
            </Button>
          </Space>
        );
      },
    },
  ];

  const getActionButtons = () => {
    let buttons = [];
    const isDisabled = purchaseOrder?.orderStatus?.name !== "Open";
    buttons.push(
      <Popconfirm
        title="Are you sure to add the changes?"
        onConfirm={handleFormSubmit}
        key="add-confirm"
        disabled={isDisabled}
      >
        <Button
          key="1"
          type="primary"
          htmlType="submit"
          loading={isLoading}
          disabled={isDisabled}
        >
          Save
        </Button>
      </Popconfirm>
    );
    buttons.push(
      <Button key="2" htmlType="button" onClick={onCancel}>
        Cancel
      </Button>
    );

    return buttons;
  };

  return (
    <div>
      <PageHeader
        title={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/dashboard">Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/purchase-orders">Purchase Orders Search</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`${path}`.replace(":id", id)}>
                {purchaseOrder?.orderNumber?.code} ::{" "}
                <Tag
                  color={`${
                    purchaseOrder?.orderStatus?.code === "OPEN"
                      ? "#2db7f5"
                      : purchaseOrder?.orderStatus?.code === "IN_PROGRESS"
                      ? "#108ee9"
                      : "#87d068"
                  }`}
                >
                  {purchaseOrder?.orderStatus?.code}
                </Tag>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={getActionButtons()}
      >
        <Page inner error={error}>
          <TabMenu
            menu="summary"
            id={id}
            status={purchaseOrder?.orderStatus?.code}
          />
          <Form
            layout="vertical"
            form={form}
            name="form-create"
            requiredMark={false}
          >
            <Row gutter={24}>
              {/* consignee */}
              <Col span={6}>
                <Form.Item
                  name={["consignee", "id"]}
                  label="Consignee"
                  // rules={[{ required: true, message: "Consignee is required" }]}
                >
                  <Select
                    placeholder="Please select"
                    allowClear
                    loading={isLoadingConsignees}
                  >
                    {consignees.length > 0 &&
                      consignees.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* store */}
              <Col span={6}>
                <Form.Item
                  name={["store", "id"]}
                  label="Store"
                  rules={[{ required: true, message: "Store is required" }]}
                >
                  <Select
                    placeholder="Please select"
                    onChange={getStoreAddress}
                    allowClear
                    loading={isLoadingStores}
                  >
                    {stores.length > 0 &&
                      stores.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* supplier */}
              <Col span={6}>
                <Form.Item
                  name={["supplier", "id"]}
                  label="Supplier Company"
                  rules={[
                    { required: true, message: "Supplier Company is required" },
                  ]}
                >
                  <Select
                    placeholder="Please select"
                    onChange={getSupplierData}
                    allowClear
                    loading={isLoadingSuppliers}
                  >
                    {suppliers.length > 0 &&
                      suppliers.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* order date */}
              <Col span={6}>
                <Form.Item
                  name="orderDate"
                  label="Order Date"
                  rules={[
                    { required: true, message: "Order date is required" },
                  ]}
                  initialValue={moment()}
                >
                  <DatePicker
                    placeholder="Order date"
                    format="YYYY-MM-DD"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              {/* buyer */}
              <Col span={6}>
                <Form.Item
                  name={["buyer", "id"]}
                  label="Buyer"
                  // rules={[{ required: true, message: "Consignee is required" }]}
                >
                  <Select
                    placeholder="Please select"
                    allowClear
                    loading={isLoadingBuyer}
                  >
                    {buyers.length > 0 &&
                      buyers.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* delivery address */}
              <Col span={6}>
                <Form.Item
                  name={["deliveryAddress", "id"]}
                  label="Delivery Address"
                  rules={[
                    { required: true, message: "Delivery Address is required" },
                  ]}
                >
                  <Select
                    placeholder="Please select"
                    allowClear
                    loading={isLoadingStoreAddresses}
                  >
                    {storeAddresses.length > 0 &&
                      storeAddresses.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.address}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* supplier address */}
              <Col span={6}>
                <Form.Item
                  name={["supplierAddress", "id"]}
                  label="Deliver From"
                  rules={[
                    { required: true, message: "Supplier Address is required" },
                  ]}
                >
                  <Select
                    placeholder="Please select"
                    allowClear
                    loading={isLoadingSupplierData}
                  >
                    {supplierAddresses.length > 0 &&
                      supplierAddresses.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.address}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* expected date */}
              <Col span={6}>
                <Form.Item
                  name="expectedDate"
                  label="Expected Date"
                  rules={[
                    { required: true, message: "Expected date is required" },
                  ]}
                  initialValue={moment()}
                >
                  <DatePicker
                    placeholder="Expected date"
                    format="YYYY-MM-DD"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              {/* po type */}
              <Col span={6}>
                <Form.Item
                  name={["purchaseOrderType", "id"]}
                  label="Purchase Order Type"
                  rules={[
                    {
                      required: true,
                      message: "Purchase order type is required",
                    },
                  ]}
                >
                  <Select placeholder="Please select" allowClear>
                    {purchaseOrderTypes.length > 0 &&
                      purchaseOrderTypes.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* order number */}
              <Col span={6}>
                <Form.Item name="orderNumber" label="Order No">
                  <Input rows={1} placeholder="Order No" allowClear={true} />
                </Form.Item>
              </Col>

              {/* currency */}
              <Col span={6}>
                <Form.Item name={["currency", "id"]} label="Currency">
                  <Select>
                    {currencies.length > 0 &&
                      currencies.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* notes */}
              <Col span={6}>
                <Form.Item name="note" label="Notes">
                  <TextArea
                    rows={1}
                    placeholder="Enter notes"
                    allowClear={true}
                  />
                </Form.Item>
              </Col>

              {/* total price */}
              <Col span={6}>
                <Form.Item name="totalPrice" label="Total Price">
                  <InputNumber
                    value={totalPrice}
                    disabled
                    precision={2}
                    formatter={(val) =>
                      val !== null && val !== undefined && val !== 0
                        ? parseFloat(val).toFixed(2)
                        : ""
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            {purchaseOrderLines.length === 0 && (
              <Button
                style={{
                  width: "100%",
                  marginTop: 16,
                  marginBottom: 8,
                  color: "white",
                  backgroundColor: "#13c2c2",
                }}
                type="dashed"
                onClick={handleAddPurchaseOrderLine}
                icon={<PlusOutlined />}
                disabled={supplierStockTypes.length <= 0}
              >
                Add purchase order line
              </Button>
            )}

            {purchaseOrderLines.length > 0 && (
              <Table
                bordered
                pagination={false}
                columns={purchaseOrderLineColumns}
                dataSource={purchaseOrderLines}
                size="small"
              />
            )}
          </Form>
        </Page>
      </PageHeader>
    </div>
  );
}

// validating the props
Summary.propTypes = {
  history: PropTypes.isRequired,
};

export default Summary;
